import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { HeartHandshake, Search, X } from "lucide-react";

import {
  EVIDENCE_LABEL,
  EXISTING_THERAPY_CARDS,
  GROUP_BLURB,
  GROUP_LABEL,
  THERAPIES,
  THERAPY_GROUP_ORDER,
  type EvidenceTier,
  type TherapyGroup,
} from "@/data/learn/therapies";

export const Route = createFileRoute("/learn/therapies/")({
  head: () => ({
    meta: [
      { title: "Therapies & Healing Approaches — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Plain-English guides to trauma therapies — EMDR, TF-CBT, CPT, Somatic Experiencing, Prolonged Exposure, ACT, Schema Therapy, NET, CFT, psychodynamic and IPT — with an honest look at the evidence behind each.",
      },
      { property: "og:title", content: "Therapies & Healing Approaches — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Browse trauma therapies by approach, understand how each one works, and see how strong the evidence really is. Educational only.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/learn/therapies" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/learn/therapies" }],
  }),
  component: TherapiesIndex,
});

const EVIDENCE_STYLE: Record<EvidenceTier, string> = {
  established: "bg-gold/20 text-foreground",
  broader: "bg-muted text-foreground/80",
  emerging: "bg-background text-muted-foreground border border-border",
};

type Card = {
  key: string;
  name: string;
  abbrev?: string;
  badge: string;
  tagline: string;
  to: string;
  group: TherapyGroup;
  evidenceLabel: string;
  evidenceClass: string;
};

const CARDS: Card[] = [
  ...THERAPIES.map((t) => ({
    key: t.slug,
    name: t.name,
    abbrev: t.abbrev,
    badge: t.badge,
    tagline: t.tagline,
    to: `/learn/therapies/${t.slug}`,
    group: t.group,
    evidenceLabel: EVIDENCE_LABEL[t.evidence],
    evidenceClass: EVIDENCE_STYLE[t.evidence],
  })),
  ...EXISTING_THERAPY_CARDS.map((c) => ({
    key: c.to,
    name: c.name,
    abbrev: c.abbrev,
    badge: c.badge,
    tagline: c.tagline,
    to: c.to,
    group: c.group,
    evidenceLabel:
      c.evidenceLabel === "Established"
        ? EVIDENCE_LABEL.established
        : c.evidenceLabel === "Emerging"
          ? EVIDENCE_LABEL.emerging
          : EVIDENCE_LABEL.broader,
    evidenceClass:
      c.evidenceLabel === "Established"
        ? EVIDENCE_STYLE.established
        : c.evidenceLabel === "Emerging"
          ? EVIDENCE_STYLE.emerging
          : EVIDENCE_STYLE.broader,
  })),
];

function TherapyCard({ card }: { card: Card }) {
  return (
    <Link
      to={card.to}
      className="group flex h-full flex-col rounded-2xl border border-border/70 bg-card/70 p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-gold/60 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground/70">
          {card.badge}
        </span>
      </div>
      <h3 className="mt-2 font-serif text-lg leading-snug text-foreground">
        {card.abbrev ? `${card.abbrev} — ` : ""}
        {card.name}
      </h3>
      <p className="mt-1 flex-1 text-sm leading-relaxed text-foreground/70">{card.tagline}</p>
      <span className={`mt-3 inline-block w-fit rounded-full px-2 py-0.5 text-[10px] ${card.evidenceClass}`}>
        {card.evidenceLabel}
      </span>
      <span className="mt-3 inline-block text-sm font-semibold text-foreground/80 underline-offset-4 group-hover:underline">
        Learn about {card.abbrev ?? card.name} →
      </span>
    </Link>
  );
}

function Rail({ cards }: { cards: Card[] }) {
  return (
    <ul className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden [&>li]:w-[78%] [&>li]:shrink-0 [&>li]:snap-start sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 sm:pb-0 sm:[&>li]:w-auto lg:grid-cols-3">
      {cards.map((c) => (
        <li key={c.key}>
          <TherapyCard card={c} />
        </li>
      ))}
    </ul>
  );
}

function TherapiesIndex() {
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState<TherapyGroup | "all">("all");

  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      CARDS.filter((c) => {
        if (group !== "all" && c.group !== group) return false;
        if (!q) return true;
        return (
          c.name.toLowerCase().includes(q) ||
          (c.abbrev ?? "").toLowerCase().includes(q) ||
          c.badge.toLowerCase().includes(q) ||
          c.tagline.toLowerCase().includes(q)
        );
      }),
    [q, group],
  );

  const grouped = THERAPY_GROUP_ORDER.map((g) => ({
    group: g,
    cards: filtered.filter((c) => c.group === g),
  })).filter((s) => s.cards.length > 0);

  return (
    <main className="mx-auto max-w-6xl px-4 py-10">
      <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted-foreground">
        <Link to="/learn" className="hover:text-foreground hover:underline">
          Learn
        </Link>
        <span aria-hidden="true"> / </span>
        <span className="text-foreground">Therapies &amp; Healing Approaches</span>
      </nav>

      <header className="mb-8">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <HeartHandshake className="h-3.5 w-3.5" /> Therapies &amp; Healing Approaches
        </div>
        <h1 className="font-serif text-4xl tracking-tight text-foreground md:text-5xl">
          Therapies &amp; Healing Approaches
        </h1>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
          Plain-English guides to the therapies people are most often offered after trauma — what
          each one actually involves, who it may suit, and how strong the evidence really is. These
          approaches are not equivalent, and we say so on every page.
        </p>
      </header>

      <Link
        to="/learn/therapies/assessment"
        className="mb-8 block rounded-2xl border border-gold/50 bg-card p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-gold hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-6"
      >
        <h2 className="font-serif text-2xl leading-snug text-foreground">
          🧭 Find a Therapy Approach That May Fit You
        </h2>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground/80">
          Not sure where to start? Take our guided assessment to explore approaches that may match
          your needs and preferences.
        </p>
        <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-background">
          Start Assessment →
        </span>
        <span className="mt-3 block text-xs text-muted-foreground">
          Educational only — not a diagnosis, clinical assessment or treatment recommendation.
        </span>
      </Link>



      <section
        aria-label="How to read the evidence labels"
        className="mb-8 grid gap-3 sm:grid-cols-3"
      >
        {(Object.keys(EVIDENCE_LABEL) as EvidenceTier[]).map((tier) => (
          <div key={tier} className="rounded-2xl border border-border/70 bg-card/50 p-4">
            <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] ${EVIDENCE_STYLE[tier]}`}>
              {EVIDENCE_LABEL[tier]}
            </span>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              {tier === "established"
                ? "Recommended for PTSD in major clinical guidelines, with multiple randomised trials."
                : tier === "broader"
                  ? "Well-established psychotherapy, but less trauma-specific trial evidence."
                  : "Promising and widely used, with a smaller or more mixed research base."}
            </p>
          </div>
        ))}
      </section>

      <div className="mb-5">
        <label htmlFor="therapy-search" className="sr-only">
          Search therapies
        </label>
        <div className="relative">
          <Search
            className="pointer-events-none absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            id="therapy-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search — EMDR, somatic, shame, exposure…"
            className="min-h-12 w-full rounded-full border border-border bg-card/70 ps-11 pe-11 text-sm text-foreground shadow-sm outline-none transition focus-visible:border-gold/60 focus-visible:ring-2 focus-visible:ring-ring"
          />
          {q ? (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute end-3 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-muted-foreground transition hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>

      <div
        role="tablist"
        aria-label="Filter by approach"
        className="-mx-4 mb-8 flex gap-2 overflow-x-auto px-4 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:flex-wrap sm:px-0"
      >
        {(["all", ...THERAPY_GROUP_ORDER] as const).map((g) => (
          <button
            key={g}
            type="button"
            role="tab"
            aria-selected={group === g}
            onClick={() => setGroup(g)}
            className={`min-h-10 shrink-0 rounded-full border px-3.5 text-sm transition ${
              group === g
                ? "border-gold/70 bg-gold/15 text-foreground"
                : "border-border bg-card/60 text-muted-foreground hover:text-foreground"
            }`}
          >
            {g === "all" ? "All approaches" : GROUP_LABEL[g]}
          </button>
        ))}
      </div>

      {grouped.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No approaches match that search yet — try another word.
        </p>
      ) : (
        <div className="space-y-10">
          {grouped.map((section) => (
            <section key={section.group} aria-labelledby={`grp-${section.group}`}>
              <h2 id={`grp-${section.group}`} className="font-serif text-2xl text-foreground">
                {GROUP_LABEL[section.group]}
              </h2>
              <p className="mb-3 mt-1 text-sm text-muted-foreground">{GROUP_BLURB[section.group]}</p>
              <Rail cards={section.cards} />
            </section>
          ))}
        </div>
      )}

      <p className="mt-12 rounded-2xl border border-border bg-muted/30 p-5 text-center text-xs leading-relaxed text-muted-foreground">
        Recovery Trauma™ is a wellbeing and educational platform. It is not a crisis service,
        therapy, diagnosis, or medical treatment. These pages are psychoeducation — reading them is
        not the same as receiving psychotherapy from a qualified professional, and nothing here can
        tell you which therapy is right for you. If you need urgent support, please visit our{" "}
        <Link to="/crisis" className="text-gold underline-offset-2 hover:underline">
          Crisis Resources
        </Link>{" "}
        page.
      </p>
    </main>
  );
}
